"use client";
import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, Video, Image, Newspaper, Key, CheckCircle, Settings, CreditCard, Star, PlusCircle, List as ListIcon, Edit2, FileSignature, HelpCircle, Scale, Briefcase } from "lucide-react";

type NavItem = {
  key:
    | "home"
    | "videos"
    | "galleries"
    | "posts"
    | "managepassword"
    | "verifyaccount"
    | "releases"
    | "messagepreferences"
    | "payments"
    | "plans";
  href: string;
  label: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  children?: Array<{ href: string; label: string; Icon?: React.FC<React.SVGProps<SVGSVGElement>> }>;
};

const items: NavItem[] = [
  // 2026-05-28: Profile Home href points at /profile (the literal route that
  // renders ProfileDetails with the merged dashboard cards). Was /profile/home
  // before W1; the prior c45320c hotfix added a redundant "Dashboard" entry
  // that's now collapsed back into this single Profile Home entry since /profile
  // is the unified landing.
  { key: "home", label: "Profile Home", href: "/profile", Icon: Home, children: [
    { href: "/profile/editprofile", label: "Edit Profile", Icon: Edit2 },
    { href: "/profile/managepassword", label: "Manage Password", Icon: Key },
    { href: "/profile/messagepreferences", label: "Msg Preferences", Icon: Settings },
    { href: "/profile/payments", label: "Payment Cards", Icon: CreditCard },
    { href: "/profile/plans", label: "Monthly limits", Icon: Star },
    { href: "/profile/producer", label: "Producer account", Icon: Briefcase },
  ] },
  {
    key: "videos",
    label: "Your Videos",
    href: "/profile/videos",
    Icon: Video,
    children: [
      { href: "/profile/videos/create", label: "Create Video", Icon: PlusCircle },
    ],
  },
  {
    key: "galleries",
    label: "Your Galleries",
    href: "/profile/galleries",
    Icon: Image,
    children: [
      { href: "/profile/galleries/create", label: "Create Gallery", Icon: PlusCircle },
    ],
  },
  {
    key: "posts",
    label: "Your Posts",
    href: "/profile/posts",
    Icon: Newspaper,
    children: [
      { href: "/profile/posts/new", label: "Create Post", Icon: PlusCircle },
    ],
  },
  {
    key: "verifyaccount",
    label: "Verification",
    href: "/profile/verifyaccount",
    Icon: CheckCircle,
    // Submenu hidden 2026-05-01: Model Releases / Learn More / 2257 Compliance
    // sub-items removed because the new attestation modal handles per-content
    // §2257 inline. The top-level Verification entry (linking to
    // /profile/verifyaccount) stays for users who haven't completed ID verification.
    // The /profile/releases, /profile/about-verification, and /compliance/solo/me
    // routes still exist; they're just not surfaced in the sidebar.
  },
];

export default function ProfileSectionNav() {
  const pathname = usePathname();

  return (
    <nav aria-label="Profile sections" className="sticky top-header md:top-20 z-30">
      <ul className="space-y-1.5">
        {items.map(({ href, label, Icon, key, children }) => {
          const isVideos = key === 'videos';
          const isGalleries = key === 'galleries';
          const isPosts = key === 'posts';
          const isHome = key === 'home';
          const isVerify = key === 'verifyaccount';
          const baseGroupActive = pathname === href || pathname.startsWith(href + '/');
          // Special-cases: only mark parent active on exact base path
          const isVideoRoot = isVideos && pathname === '/profile/videos';
          const isGalleryRoot = isGalleries && pathname === '/profile/galleries';
          const isPostRoot = isPosts && pathname === '/profile/posts';
          const isVideoCreate = isVideos && (pathname === '/profile/videos/create' || pathname.startsWith('/profile/videos/create/'));
          const isGalleryCreate = isGalleries && (pathname === '/profile/galleries/create' || pathname.startsWith('/profile/galleries/create/'));
          const isPostCreate = isPosts && (pathname === '/profile/posts/new' || pathname.startsWith('/profile/posts/new/'));
          const isHomeEdit = isHome && pathname === '/profile/editprofile';
          // Home's href is now /profile (was /profile/home). startsWith would
          // highlight Home on every profile sub-route; require exact match OR
          // legacy /profile/home + its sub-items (editprofile / managepassword
          // / etc. which still live at /profile/{section}).
          const groupActive = isHome
            ? (pathname === '/profile' || pathname === '/profile/home' || isHomeEdit
                || pathname === '/profile/managepassword'
                || pathname === '/profile/messagepreferences'
                || pathname === '/profile/payments'
                || pathname === '/profile/plans'
                || pathname === '/profile/producer')
            : ((isVideos || isGalleries || isPosts) ? (pathname === href) : baseGroupActive);
          const hasChildren = Array.isArray(children) && children.length > 0;
          const submenuId = `${key}-submenu`;
          // Keep submenu open on any relevant route, otherwise only when active
          const openSubmenu = isVideos
            ? pathname.startsWith('/profile/videos')
            : isGalleries
              ? pathname.startsWith('/profile/galleries')
              : isPosts
                ? pathname.startsWith('/profile/posts')
              : isHome
                ? (pathname === '/profile' || pathname.startsWith('/profile/home') || isHomeEdit || pathname === '/profile/managepassword' || pathname === '/profile/messagepreferences' || pathname === '/profile/payments' || pathname === '/profile/plans' || pathname === '/profile/producer')
              : isVerify
                ? pathname.startsWith('/profile/verifyaccount') || pathname.startsWith('/profile/releases') || pathname.startsWith('/profile/about-verification') || pathname.startsWith('/compliance/solo/')
                : groupActive;
          return (
            <li key={href}>
              <Link
                href={href}
                className={
                  `group relative flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all duration-200 border ` +
                  (
                    (isVideos && isVideoRoot) || (isGalleries && isGalleryRoot) || (isPosts && isPostRoot) || groupActive
                      ? 'font-semibold btn-ribbon text-white border-transparent shadow-sm'
                      : 'bg-card text-foreground/75 border-border hover:border-primary/40 hover:text-foreground hover:shadow-sm hover:translate-x-0.5'
                  )
                }
                aria-current={((isVideoRoot || isGalleryRoot || isPostRoot) || (groupActive && !isVideos && !isGalleries && !isPosts)) ? 'page' : undefined}
              >
                <Icon className="w-4 h-4" />
                <span className="truncate group-hover:translate-x-0.5 transition-transform duration-200">{label}</span>
              </Link>

              {hasChildren && openSubmenu && (
                <ul id={submenuId} className="mt-1 ml-7 space-y-1">
                  {(
                    isVideos && isVideoRoot
                      ? children!.filter(c => c.href !== '/profile/videos')
                      : isGalleries && isGalleryRoot
                        ? children!.filter(c => c.href !== '/profile/galleries')
                        : isPosts && isPostRoot
                          ? children!.filter(c => c.href !== '/profile/posts')
                        : children!
                  )!.map((c) => {
                    const isBase = c.href === href; // e.g., /profile/videos for "All Videos"
                    const active = isBase
                      ? pathname === href // only highlight base on exact match
                      : (pathname === c.href || pathname.startsWith(c.href + '/'));
                    const CIcon = c.Icon || ListIcon;
                    const inactiveClasses = 'text-muted-foreground hover:text-foreground hover:bg-foreground/[0.05]';
                    const isCreateChild = (isVideos && c.href === '/profile/videos/create' && isVideoCreate)
                      || (isGalleries && c.href === '/profile/galleries/create' && isGalleryCreate)
                      || (isPosts && c.href === '/profile/posts/new' && isPostCreate)
                      || (isHome && c.href === '/profile/editprofile' && isHomeEdit);
                    const isVerifyChildActive = key === 'verifyaccount' && (active || pathname === c.href);
                    return (
                      <li key={c.href}>
                        <Link
                          href={c.href}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            (isCreateChild || isVerifyChildActive || active)
                              ? 'ring-chip font-medium shadow-sm'
                              : inactiveClasses
                          }`}
                          aria-current={(isCreateChild || (active && !isVideos && !isGalleries && !isPosts)) ? 'page' : undefined}
                        >
                          <CIcon className="w-3.5 h-3.5" />
                          <span className="truncate">{c.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
